'use client';

import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import {
  CellType,
  NUM_PHEROMONE_CHANNELS,
  DEFAULT_CONFIG,
  type World,
  type Program,
  type SimResult,
} from '@/lib/swarm/types';
import {
  createWorld,
  stepTick,
  cloneMap,
} from '@/lib/swarm/simulation';
import { generatePracticeMaps } from '@/lib/swarm/maps';
import {
  captureSnapshot,
  restoreSnapshot,
  findNearestSnapshot,
  SNAPSHOT_INTERVAL,
  type WorldSnapshot,
} from '@/lib/swarm/snapshots';
import { getSimColors, foodColor } from '@/lib/sim-colors';

const PRACTICE_SEED_BASE = 42;
const PRACTICE_MAP_COUNT = 12;
const CELL_SIZE = 5;
const THUMB_CELL = 2;

interface MapState {
  world: World;
  snapshots: WorldSnapshot[];
  bufferWorld: World;
}

interface SwarmSimPanelProps {
  program: Program | null;
  tagNames: Map<number, string>;
  selectedAntIndex: number | null;
  onSelectAnt: (index: number | null) => void;
  onWorldUpdate: (world: World | null) => void;
  onScoresUpdate: (scores: Map<number, SimResult>) => void;
  onScoresStale: () => void;
}

export function getInstructionText(program: Program, sourceCode: string, pc: number, overrideSourceLines?: number[]): string {
  if (pc < 0 || pc >= program.instructions.length) return '(end)';
  const sourceLines = overrideSourceLines ?? program.sourceLines;
  if (sourceLines && sourceLines[pc]) {
    const lineNum = sourceLines[pc];
    const lines = sourceCode.split('\n');
    if (lineNum > 0 && lineNum <= lines.length) {
      return lines[lineNum - 1].trim();
    }
  }
  return `instruction ${pc}`;
}

export function SwarmSimPanel({
  program,
  tagNames,
  selectedAntIndex,
  onSelectAnt,
  onWorldUpdate,
  onScoresUpdate,
  onScoresStale,
}: SwarmSimPanelProps) {
  const [seedBase, setSeedBase] = useState(PRACTICE_SEED_BASE);
  const maps = useRef(
    generatePracticeMaps(
      DEFAULT_CONFIG.mapWidth, DEFAULT_CONFIG.mapHeight,
      PRACTICE_SEED_BASE, PRACTICE_MAP_COUNT,
    )
  );
  const seedBaseRef = useRef(seedBase);

  const mapStatesRef = useRef<MapState[]>([]);
  const [simState, setSimState] = useState<'idle' | 'running' | 'paused'>('idle');
  const [speed, setSpeed] = useState(5);
  const [focusedMap, setFocusedMap] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [maxComputedTick, setMaxComputedTick] = useState(0);
  const [pherChannels, setPherChannels] = useState<Set<number>>(new Set());
  const [activeTag, setActiveTag] = useState<number | null>(null);

  const simStateRef = useRef(simState);
  const speedRef = useRef(speed);
  const pherChannelsRef = useRef(pherChannels);
  const activeTagRef = useRef(activeTag);
  const selectedAntRef = useRef(selectedAntIndex);
  const focusedMapRef = useRef(focusedMap);
  const maxComputedTickRef = useRef(maxComputedTick);
  const tickRef = useRef(tick);
  const animRef = useRef<number>(0);
  const drawDirtyRef = useRef(true);

  if (simState !== simStateRef.current || pherChannels !== pherChannelsRef.current
    || activeTag !== activeTagRef.current || selectedAntIndex !== selectedAntRef.current
    || focusedMap !== focusedMapRef.current || tick !== tickRef.current) {
    drawDirtyRef.current = true;
  }
  simStateRef.current = simState;
  speedRef.current = speed;
  pherChannelsRef.current = pherChannels;
  activeTagRef.current = activeTag;
  maxComputedTickRef.current = maxComputedTick;
  tickRef.current = tick;
  selectedAntRef.current = selectedAntIndex;
  focusedMapRef.current = focusedMap;
  seedBaseRef.current = seedBase;

  // Thumb canvases (grid view)
  const thumbCanvasRefs = useRef<(HTMLCanvasElement | null)[]>(new Array(PRACTICE_MAP_COUNT).fill(null));
  // Focus canvas (detail view)
  const focusCanvasRef = useRef<HTMLCanvasElement>(null);

  const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startBuffering = useCallback((states: MapState[]) => {
    if (bufferTimerRef.current !== null) {
      clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = null;
    }
    const BATCH_TIME_MS = 8;
    const runBatch = () => {
      const deadline = performance.now() + BATCH_TIME_MS;
      let allDone = true;
      while (performance.now() < deadline) {
        let anyAdvanced = false;
        for (const state of states) {
          if (state.bufferWorld.tick >= DEFAULT_CONFIG.maxTicks) continue;
          anyAdvanced = true;
          stepTick(state.bufferWorld, DEFAULT_CONFIG);
          if (state.bufferWorld.tick % SNAPSHOT_INTERVAL === 0) {
            state.snapshots.push(captureSnapshot(state.bufferWorld));
          }
        }
        if (!anyAdvanced) break;
      }
      let minTick = DEFAULT_CONFIG.maxTicks;
      for (const state of states) {
        if (state.bufferWorld.tick < minTick) minTick = state.bufferWorld.tick;
        if (state.bufferWorld.tick < DEFAULT_CONFIG.maxTicks) allDone = false;
      }
      setMaxComputedTick(minTick);
      if (!allDone) {
        bufferTimerRef.current = setTimeout(runBatch, 4);
      } else {
        bufferTimerRef.current = null;
        const scores = new Map<number, SimResult>();
        for (let i = 0; i < states.length; i++) {
          const bw = states[i].bufferWorld;
          const m = maps.current[i];
          scores.set(i, {
            foodCollected: bw.foodCollected,
            totalFood: m.totalFood,
            ticks: bw.tick,
            mapSeed: m.seed,
            mapName: m.name,
          });
        }
        onScoresUpdate(scores);
      }
    };
    bufferTimerRef.current = setTimeout(runBatch, 0);
  }, [onScoresUpdate]);

  const initWorlds = useCallback(() => {
    if (!program) return;
    onScoresStale();
    if (bufferTimerRef.current !== null) {
      clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = null;
    }
    maps.current = generatePracticeMaps(
      DEFAULT_CONFIG.mapWidth, DEFAULT_CONFIG.mapHeight,
      seedBaseRef.current, PRACTICE_MAP_COUNT,
    );
    const states: MapState[] = [];
    for (let i = 0; i < maps.current.length; i++) {
      const world = createWorld(cloneMap(maps.current[i]), program, DEFAULT_CONFIG);
      const bufferWorld = createWorld(cloneMap(maps.current[i]), program, DEFAULT_CONFIG);
      states.push({ world, snapshots: [captureSnapshot(world)], bufferWorld });
    }
    mapStatesRef.current = states;
    drawDirtyRef.current = true;
    setTick(0);
    setMaxComputedTick(0);
    setSimState('idle');
    const f = focusedMapRef.current;
    if (f !== null && states[f]) {
      onWorldUpdate(states[f].world);
    } else {
      onWorldUpdate(null);
    }
    startBuffering(states);
  }, [program, seedBase, onWorldUpdate, onScoresStale, startBuffering]);

  useEffect(() => {
    initWorlds();
    return () => {
      if (bufferTimerRef.current !== null) {
        clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = null;
      }
    };
  }, [initWorlds]);

  const drawWorld = useCallback((
    canvas: HTMLCanvasElement,
    world: World,
    cellSize: number,
    showAnts: boolean,
    pherChs: Set<number>,
    selectedAnt: number | null,
    filterTag: number | null,
  ) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const colors = getSimColors();
    const { map, ants } = world;
    const w = map.width;
    const h = map.height;

    ctx.fillStyle = colors.empty;
    ctx.fillRect(0, 0, w * cellSize, h * cellSize);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        const cell = map.cells[idx];
        let color: string | null = null;

        if (cell === CellType.WALL) color = colors.wall;
        else if (cell === CellType.NEST) color = colors.nest;
        else if (map.food[idx] > 0) {
          color = foodColor(colors, map.food[idx]);
        }

        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
        }
      }
    }

    if (pherChs.size > 0 && cellSize > 1) {
      for (const pherCh of pherChs) {
        if (pherCh < 0 || pherCh >= NUM_PHEROMONE_CHANNELS) continue;
        const [r, g, b] = colors.pheromone[pherCh];
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const val = map.pheromones[(y * w + x) * NUM_PHEROMONE_CHANNELS + pherCh];
            if (val > 0) {
              const alpha = (val / 255) * 0.6;
              ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
              ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
            }
          }
        }
      }
    }

    if (showAnts) {
      for (let i = 0; i < ants.length; i++) {
        const ant = ants[i];
        const dimmed = filterTag !== null && ant.tag !== filterTag;
        if (i === selectedAnt) {
          ctx.fillStyle = colors.antSelected;
          ctx.fillRect(ant.x * cellSize, ant.y * cellSize, cellSize, cellSize);
        } else if (dimmed) {
          ctx.fillStyle = colors.antDimmed;
          if (cellSize > 2) {
            ctx.fillRect(ant.x * cellSize + 1, ant.y * cellSize + 1, cellSize - 2, cellSize - 2);
          } else {
            ctx.fillRect(ant.x * cellSize, ant.y * cellSize, cellSize, cellSize);
          }
        } else {
          ctx.fillStyle = ant.carrying ? colors.antCarrying : colors.ant;
          if (cellSize > 2) {
            ctx.fillRect(ant.x * cellSize + 1, ant.y * cellSize + 1, cellSize - 2, cellSize - 2);
          } else {
            ctx.fillRect(ant.x * cellSize, ant.y * cellSize, cellSize, cellSize);
          }
        }
      }
    }
  }, []);

  const restoreToTick = useCallback((states: MapState[], targetTick: number, focusedOnly: boolean) => {
    const restore = (state: MapState) => {
      if (state.world.tick === targetTick) return;
      if (targetTick < state.world.tick || targetTick - state.world.tick > SNAPSHOT_INTERVAL) {
        const snap = findNearestSnapshot(state.snapshots, targetTick);
        if (snap) restoreSnapshot(state.world, snap);
      }
      while (state.world.tick < targetTick && state.world.tick < DEFAULT_CONFIG.maxTicks) {
        stepTick(state.world, DEFAULT_CONFIG);
      }
    };
    if (focusedOnly && focusedMapRef.current !== null) {
      const state = states[focusedMapRef.current];
      if (state) restore(state);
    } else {
      for (const state of states) restore(state);
    }
  }, []);

  const gameLoop = useCallback(() => {
    const states = mapStatesRef.current;
    if (states.length === 0) {
      animRef.current = requestAnimationFrame(gameLoop);
      return;
    }

    if (simStateRef.current === 'running') {
      const newTick = Math.min(
        tickRef.current + speedRef.current,
        maxComputedTickRef.current,
        DEFAULT_CONFIG.maxTicks,
      );

      if (newTick > tickRef.current) {
        restoreToTick(states, newTick, focusedMapRef.current !== null);
        setTick(newTick);
        tickRef.current = newTick;
        drawDirtyRef.current = true;

        const refWorld = states[focusedMapRef.current ?? 0]?.world;
        if (refWorld && focusedMapRef.current !== null) {
          onWorldUpdate(refWorld);
        }
      }

      if (newTick >= DEFAULT_CONFIG.maxTicks) {
        setSimState('paused');
      }
    }

    if (drawDirtyRef.current) {
      drawDirtyRef.current = false;
      const focused = focusedMapRef.current;
      if (focused !== null) {
        const canvas = focusCanvasRef.current;
        const state = states[focused];
        if (canvas && state) {
          drawWorld(canvas, state.world, CELL_SIZE, true, pherChannelsRef.current, selectedAntRef.current, activeTagRef.current);
        }
      } else {
        restoreToTick(states, tickRef.current, false);
        for (let i = 0; i < states.length; i++) {
          const canvas = thumbCanvasRefs.current[i];
          if (canvas) {
            drawWorld(canvas, states[i].world, THUMB_CELL, true, new Set(), null, null);
          }
        }
      }
    }

    animRef.current = requestAnimationFrame(gameLoop);
  }, [drawWorld, restoreToTick, onWorldUpdate]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animRef.current);
  }, [gameLoop]);

  const handlePlay = useCallback(() => {
    if (tickRef.current >= DEFAULT_CONFIG.maxTicks) {
      const states = mapStatesRef.current;
      if (states.length === 0) return;
      restoreToTick(states, 0, false);
      setTick(0);
      tickRef.current = 0;
      drawDirtyRef.current = true;
      const refWorld = states[focusedMapRef.current ?? 0]?.world;
      if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
      setSimState('running');
    } else {
      setSimState(s => s === 'running' ? 'paused' : 'running');
    }
  }, [restoreToTick, onWorldUpdate]);

  const handleRestart = useCallback(() => {
    initWorlds();
  }, [initWorlds]);

  const handleSkipToStart = useCallback(() => {
    const states = mapStatesRef.current;
    if (states.length === 0) return;
    restoreToTick(states, 0, false);
    setTick(0);
    tickRef.current = 0;
    drawDirtyRef.current = true;
    const refWorld = states[focusedMapRef.current ?? 0]?.world;
    if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
    setSimState('paused');
  }, [restoreToTick, onWorldUpdate]);

  const handleStepForward = useCallback(() => {
    const newTick = Math.min(tick + 1, maxComputedTick, DEFAULT_CONFIG.maxTicks);
    if (newTick === tick) return;
    const states = mapStatesRef.current;
    restoreToTick(states, newTick, focusedMapRef.current !== null);
    setTick(newTick);
    drawDirtyRef.current = true;
    const refWorld = states[focusedMapRef.current ?? 0]?.world;
    if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
    setSimState('paused');
  }, [tick, maxComputedTick, restoreToTick, onWorldUpdate]);

  const handleStepBack = useCallback(() => {
    if (tick <= 0) return;
    const newTick = tick - 1;
    const states = mapStatesRef.current;
    restoreToTick(states, newTick, focusedMapRef.current !== null);
    setTick(newTick);
    drawDirtyRef.current = true;
    const refWorld = states[focusedMapRef.current ?? 0]?.world;
    if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
    setSimState('paused');
  }, [tick, restoreToTick, onWorldUpdate]);

  const scrubTarget = useRef<number | null>(null);
  const scrubRaf = useRef<number>(0);

  const applyScrub = useCallback((targetTick: number) => {
    const states = mapStatesRef.current;
    if (states.length === 0) return;
    restoreToTick(states, targetTick, focusedMapRef.current !== null);
    setTick(targetTick);
    drawDirtyRef.current = true;
    const refWorld = states[focusedMapRef.current ?? 0]?.world;
    if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
    setSimState('paused');
  }, [restoreToTick, onWorldUpdate]);

  const handleScrub = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const target = Math.min(parseInt(e.target.value), maxComputedTick);
    scrubTarget.current = target;
    setTick(target);
    if (!scrubRaf.current) {
      scrubRaf.current = requestAnimationFrame(() => {
        scrubRaf.current = 0;
        if (scrubTarget.current !== null) {
          applyScrub(scrubTarget.current);
          scrubTarget.current = null;
        }
      });
    }
  }, [maxComputedTick, applyScrub]);

  const handleSkipToEnd = useCallback(() => {
    if (!program || maxComputedTick === 0) return;
    const states = mapStatesRef.current;
    if (states.length === 0) return;

    restoreToTick(states, maxComputedTick, false);
    setTick(maxComputedTick);
    drawDirtyRef.current = true;

    const refWorld = states[focusedMapRef.current ?? 0]?.world;
    if (refWorld && focusedMapRef.current !== null) onWorldUpdate(refWorld);
    setSimState('paused');
  }, [program, maxComputedTick, restoreToTick, onWorldUpdate]);

  const handleFocus = useCallback((idx: number) => {
    setFocusedMap(idx);
    drawDirtyRef.current = true;
    const state = mapStatesRef.current[idx];
    if (state) {
      setTick(state.world.tick);
      onWorldUpdate(state.world);
    }
  }, [onWorldUpdate]);

  const handleUnfocus = useCallback(() => {
    // Restore all maps to current tick so grid shows correct food scores
    const states = mapStatesRef.current;
    if (states.length > 0) {
      restoreToTick(states, tickRef.current, false);
    }
    setFocusedMap(null);
    drawDirtyRef.current = true;
    onWorldUpdate(null);
    onSelectAnt(null);
  }, [onWorldUpdate, onSelectAnt, restoreToTick]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedMap !== null) {
        handleUnfocus();
      }
      if (e.key === ' ' && !(e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        handlePlay();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedMap, handleUnfocus, handlePlay]);

  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (focusedMap === null) return;
    drawDirtyRef.current = true;
    const state = mapStatesRef.current[focusedMap];
    if (!state) return;
    const canvas = focusCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const cx = Math.floor((e.clientX - rect.left) * scaleX / CELL_SIZE);
    const cy = Math.floor((e.clientY - rect.top) * scaleY / CELL_SIZE);

    for (let i = 0; i < state.world.ants.length; i++) {
      if (state.world.ants[i].x === cx && state.world.ants[i].y === cy) {
        onSelectAnt(selectedAntRef.current === i ? null : i);
        return;
      }
    }
    onSelectAnt(null);
  }, [focusedMap, onSelectAnt]);

  const canvasW = DEFAULT_CONFIG.mapWidth * CELL_SIZE;
  const canvasH = DEFAULT_CONFIG.mapHeight * CELL_SIZE;
  const thumbW = DEFAULT_CONFIG.mapWidth * THUMB_CELL;
  const thumbH = DEFAULT_CONFIG.mapHeight * THUMB_CELL;

  // Grid mode
  if (focusedMap === null) {
    return (
      <div className="flex flex-col min-h-0 flex-1">
        {/* Controls bar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-stroke/50 text-[11px] shrink-0">
          <div className="flex items-center gap-1">
            <CtrlBtn onClick={handleSkipToStart} label="|&lt;" title="Skip to start" />
            <CtrlBtn onClick={handleStepBack} label="&lt;" title="Step back" />
            <CtrlBtn
              onClick={handlePlay}
              label={simState === 'running' ? '\u275A\u275A' : '\u25B6'}
              title={simState === 'running' ? 'Pause' : 'Play'}
              active={simState === 'running'}
              width={32}
            />
            <CtrlBtn onClick={handleStepForward} label="&gt;" title="Step forward" />
            <CtrlBtn onClick={handleSkipToEnd} label="&gt;&gt;|" title="Skip to end" />
            <span className="text-dim mx-1">|</span>
            {[1, 5, 25, 100, 500].map(s => (
              <CtrlBtn
                key={s}
                onClick={() => setSpeed(s)}
                label={`${s}x`}
                active={speed === s}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <SeedInput value={seedBase} onChange={setSeedBase} />
            <span className="text-cyan">{`Tick: ${tick}/${DEFAULT_CONFIG.maxTicks}`}</span>
          </div>
        </div>

        {/* Map grid */}
        <div className="flex-1 p-3 min-h-0 overflow-hidden flex items-center justify-center">
          <div className="grid grid-cols-4 gap-2">
            {maps.current.map((m, i) => {
              const state = mapStatesRef.current[i];
              const foodCollected = state?.world.foodCollected ?? 0;
              return (
                <button
                  key={i}
                  onClick={() => handleFocus(i)}
                  className="group relative border border-stroke/50 rounded hover:border-cyan transition-colors cursor-pointer bg-surface overflow-hidden"
                  style={{ aspectRatio: '1', lineHeight: 0 }}
                >
                  <canvas
                    ref={el => { thumbCanvasRefs.current[i] = el; }}
                    width={thumbW}
                    height={thumbH}
                    className="block w-full h-full"
                    style={{ imageRendering: 'pixelated' }}
                  />
                  <div className="absolute bottom-1 right-1 bg-surface/80 px-1 rounded text-[9px] leading-tight">
                    <span className="text-amber">{foodCollected}/{m.totalFood}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrubber */}
        <Scrubber tick={tick} maxTick={DEFAULT_CONFIG.maxTicks} buffered={maxComputedTick} onScrub={handleScrub} />
      </div>
    );
  }

  // Focus mode
  const focusedState = mapStatesRef.current[focusedMap];
  const mapName = maps.current[focusedMap]?.name ?? '';
  const foodCollected = focusedState?.world.foodCollected ?? 0;
  const totalFood = maps.current[focusedMap]?.totalFood ?? 0;

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Focus header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-stroke/50 text-[11px] shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={handleUnfocus}
            className="text-dim hover:text-primary cursor-pointer text-[10px]"
            title="Back to grid (Esc)"
          >
            &lt; grid
          </button>
          <span className="text-dim">|</span>
          <span className="text-secondary">{mapName}</span>
        </div>
        <span className="text-amber">{foodCollected}/{totalFood}</span>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex items-center justify-center p-2 min-h-0 overflow-hidden">
        <canvas
          ref={focusCanvasRef}
          width={canvasW}
          height={canvasH}
          className="block cursor-crosshair border border-stroke/50 rounded"
          style={{ imageRendering: 'pixelated', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
          onClick={handleCanvasClick}
        />
      </div>

      {/* Scrubber */}
      <Scrubber tick={tick} maxTick={DEFAULT_CONFIG.maxTicks} buffered={maxComputedTick} onScrub={handleScrub} />

      {/* Controls */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-stroke/50 text-[11px] shrink-0">
        <div className="flex items-center gap-1">
          <CtrlBtn onClick={handleSkipToStart} label="|&lt;" title="Skip to start" />
          <CtrlBtn onClick={handleStepBack} label="&lt;" title="Step back" />
          <CtrlBtn
            onClick={handlePlay}
            label={simState === 'running' ? '\u275A\u275A' : '\u25B6'}
            title={simState === 'running' ? 'Pause' : 'Play'}
            active={simState === 'running'}
            width={32}
          />
          <CtrlBtn onClick={handleStepForward} label="&gt;" title="Step forward" />
          <CtrlBtn onClick={handleSkipToEnd} label="&gt;&gt;|" title="Skip to end" />
          <span className="text-dim mx-1">|</span>
          {[1, 5, 25, 100, 500].map(s => (
            <CtrlBtn
              key={s}
              onClick={() => setSpeed(s)}
              label={`${s}x`}
              active={speed === s}
            />
          ))}
        </div>
        <span className="text-cyan">
          {`Tick: ${tick}/${DEFAULT_CONFIG.maxTicks}`}
        </span>
      </div>

      {/* Overlay toggles */}
      <div className="flex items-center px-3 py-1.5 border-t border-stroke/50 text-[10px] shrink-0">
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-dim mr-1">Pher:</span>
          {(() => {
            const colors = getSimColors();
            return Array.from({ length: NUM_PHEROMONE_CHANNELS }, (_, i) => {
              const [r, g, b] = colors.pheromone[i];
              return (
                <button
                  key={i}
                  onClick={() => { setPherChannels(prev => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  }); drawDirtyRef.current = true; }}
                  className={`w-5 h-5 rounded text-[9px] font-bold cursor-pointer border transition-colors ${
                    pherChannels.has(i)
                      ? 'border-primary'
                      : 'border-stroke/50 opacity-50 hover:opacity-80'
                  }`}
                  style={{ backgroundColor: `rgb(${r},${g},${b})`, color: '#000' }}
                >
                  {colors.pheromoneLabels[i]}
                </button>
              );
            });
          })()}
          {pherChannels.size > 0 && (
            <button
              onClick={() => { setPherChannels(new Set()); drawDirtyRef.current = true; }}
              className="text-dim hover:text-primary ml-1 cursor-pointer"
            >
              off
            </button>
          )}
        </div>
        {tagNames.size > 0 && (
          <TagFilters
            tagNames={tagNames}
            activeTag={activeTag}
            onToggle={(tagNum) => { setActiveTag(prev => prev === tagNum ? null : tagNum); drawDirtyRef.current = true; }}
          />
        )}
      </div>
    </div>
  );
}

function TagFilters({
  tagNames,
  activeTag,
  onToggle,
}: {
  tagNames: Map<number, string>;
  activeTag: number | null;
  onToggle: (tagNum: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkScroll, tagNames]);

  const scroll = useCallback((dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 80, behavior: 'smooth' });
  }, []);

  return (
    <div className="flex items-center ml-2 min-w-0">
      <span className="text-dim mr-1 shrink-0">Filter:</span>
      {canScrollLeft && (
        <button onClick={() => scroll(-1)} className="text-dim hover:text-primary cursor-pointer shrink-0 px-0.5">&lt;</button>
      )}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex items-center gap-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {Array.from(tagNames.entries()).map(([tagNum, tagName]) => (
          <button
            key={tagNum}
            onClick={() => onToggle(tagNum)}
            className={`px-1.5 h-5 rounded text-[9px] font-bold cursor-pointer border transition-colors shrink-0 ${
              activeTag === tagNum
                ? 'border-cyan text-cyan bg-cyan/10'
                : 'border-stroke/50 text-dim hover:text-primary'
            }`}
          >
            {tagName.length > 10 ? tagName.slice(0, 10) + '\u2026' : tagName}
          </button>
        ))}
      </div>
      {canScrollRight && (
        <button onClick={() => scroll(1)} className="text-dim hover:text-primary cursor-pointer shrink-0 px-0.5">&gt;</button>
      )}
    </div>
  );
}

function CtrlBtn({
  onClick,
  label,
  title,
  active,
  disabled,
  width,
}: {
  onClick: () => void;
  label: string;
  title?: string;
  active?: boolean;
  disabled?: boolean;
  width?: number;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`px-2 py-0.5 rounded border text-[11px] cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-center outline-none ${
        active
          ? 'border-cyan text-cyan bg-elevated'
          : 'border-stroke/50 text-secondary hover:text-primary hover:border-stroke-bright'
      }`}
      style={width ? { width } : undefined}
      dangerouslySetInnerHTML={{ __html: label }}
    />
  );
}

function Scrubber({
  tick,
  maxTick,
  buffered,
  onScrub,
}: {
  tick: number;
  maxTick: number;
  buffered: number;
  onScrub: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const bufferPct = maxTick > 0 ? (buffered / maxTick) * 100 : 0;
  return (
    <div className="px-3 py-1 shrink-0">
      <div className="relative h-1 w-full">
        <div className="absolute inset-0 bg-stroke rounded" />
        <div
          className="absolute top-0 left-0 h-full bg-cyan/25 rounded"
          style={{ width: `${bufferPct}%` }}
        />
        <input
          name="tick-scrubber"
          type="range"
          min={0}
          max={maxTick}
          value={tick}
          onChange={onScrub}
          onMouseUp={e => (e.target as HTMLInputElement).blur()}
          tabIndex={-1}
          className="absolute inset-0 w-full h-full appearance-none bg-transparent cursor-pointer accent-cyan [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-cyan [&::-webkit-slider-thumb]:rounded-sm"
        />
      </div>
    </div>
  );
}

export function AntInfoOverlay({
  ants,
  selectedAntIndex,
  program,
  tagNames,
  registerAliases,
  sourceCode,
  sourceLines,
  onSelectAnt,
  onJumpToLine,
}: {
  ants: import('@/lib/swarm/types').Ant[] | null;
  selectedAntIndex: number;
  program: Program;
  tagNames?: Map<number, string>;
  registerAliases?: Map<number, string>;
  sourceCode: string;
  sourceLines?: number[];
  onSelectAnt: (index: number | null) => void;
  onJumpToLine?: (line: number) => void;
}) {
  const ant = ants ? ants[selectedAntIndex] ?? null : null;

  const colocated = useMemo(() => {
    if (!ant || !ants) return [];
    return ants
      .map((a, i) => ({ a, i }))
      .filter(({ a }) => a.x === ant.x && a.y === ant.y)
      .map(({ i }) => i);
  }, [ants, ant]);

  if (!ant) return null;

  const posInGroup = colocated.indexOf(selectedAntIndex);
  const groupSize = colocated.length;

  return (
    <div
      className="h-full flex flex-col gap-1.5 px-2 py-2 bg-surface/90 border border-stroke/50 rounded text-[10px] tracking-wider overflow-y-auto"
      style={{ lineHeight: 'normal' }}
    >
      {/* Header: ant ID + actions */}
      <div className="flex items-center justify-between">
        <span className="text-amber font-bold">#{selectedAntIndex}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const tags = tagNames ?? program.tagNames;
              const aliases = registerAliases ?? program.registerAliases;
              const data = {
                id: selectedAntIndex,
                x: ant.x, y: ant.y,
                carrying: ant.carrying,
                tag: ant.tag >= 0 && tags?.has(ant.tag) ? tags.get(ant.tag) : ant.tag,
                pc: ant.pc,
                instruction: getInstructionText(program, sourceCode, ant.pc, sourceLines),
                registers: Object.fromEntries(
                  ant.regs.slice(0, 8).map((val, i) => [aliases?.get(i) ?? `r${i}`, val])
                ),
              };
              navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            }}
            className="text-dim hover:text-primary cursor-pointer"
            title="Copy ant state as JSON"
          >copy</button>
          <button
            onClick={() => onSelectAnt(null)}
            className="text-dim hover:text-primary cursor-pointer"
          >x</button>
        </div>
      </div>

      {/* Colocated nav */}
      {groupSize > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => posInGroup > 0 && onSelectAnt(colocated[posInGroup - 1])}
            disabled={posInGroup === 0}
            className="text-dim hover:text-primary disabled:opacity-30 cursor-pointer disabled:cursor-default"
          >&lt;</button>
          <span className="text-dim">{posInGroup + 1}/{groupSize}</span>
          <button
            onClick={() => posInGroup < groupSize - 1 && onSelectAnt(colocated[posInGroup + 1])}
            disabled={posInGroup === groupSize - 1}
            className="text-dim hover:text-primary disabled:opacity-30 cursor-pointer disabled:cursor-default"
          >&gt;</button>
        </div>
      )}

      {/* Status */}
      <div className="flex flex-col gap-0.5">
        <div className="flex justify-between">
          <span className="text-dim">pos</span>
          <span className="text-secondary">{ant.x},{ant.y}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-dim">carry</span>
          <span className={ant.carrying ? 'text-amber' : 'text-dim'}>{ant.carrying ? 'food' : 'empty'}</span>
        </div>
        {ant.tag >= 0 && (tagNames ?? program.tagNames)?.has(ant.tag) && (
          <div className="flex justify-between">
            <span className="text-dim">tag</span>
            <span className="font-bold text-cyan">{(tagNames ?? program.tagNames)!.get(ant.tag)}</span>
          </div>
        )}
      </div>

      {/* Current instruction */}
      <div className="border-t border-stroke/30 pt-1.5 mt-0.5">
        <div className="text-secondary truncate">{getInstructionText(program, sourceCode, ant.pc, sourceLines)}</div>
        {(() => {
          const line = (sourceLines ?? program.sourceLines)?.[ant.pc];
          return line && (
            <span>{onJumpToLine
              ? <button className="text-dim hover:text-primary cursor-pointer" onClick={() => onJumpToLine(line)}>L{line}</button>
              : <span className="text-dim">L{line}</span>
            }</span>
          );
        })()}
      </div>

      {/* Registers */}
      <div className="flex flex-col gap-0.5 border-t border-stroke/30 pt-1.5 mt-0.5">
        {ant.regs.slice(0, 8).map((val, i) => (
          <div key={i} className="flex justify-between">
            <span className="text-dim">{(registerAliases ?? program.registerAliases)?.get(i) ?? `r${i}`}</span>
            <span className={val !== 0 ? 'text-primary' : 'text-dim/40'}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeedInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const digits = String(value).padStart(3, '0').split('');
  const refs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  const commit = (newDigits: string[]) => {
    const n = parseInt(newDigits.join('') || '0');
    onChange(Math.min(isNaN(n) ? 0 : n, 999));
  };

  return (
    <div className="flex items-center text-[11px] text-dim/50 hover:text-dim transition-colors focus-within:text-amber font-mono">
      <span className="select-none">Seed&nbsp;</span>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          name={`seed-digit-${i}`}
          type="text"
          inputMode="numeric"
          value={d}
          tabIndex={-1}
          maxLength={1}
          className="w-[1ch] bg-transparent outline-none text-center border-b border-transparent focus:border-stroke cursor-pointer focus:cursor-text"
          onFocus={e => e.target.select()}
          onChange={() => {}}
          onKeyDown={e => {
            const newDigits = [...digits];
            if (e.key >= '0' && e.key <= '9') {
              e.preventDefault();
              newDigits[i] = e.key;
              commit(newDigits);
              refs[i + 1]?.current?.focus();
            } else if (e.key === 'Backspace') {
              e.preventDefault();
              newDigits[i] = '0';
              commit(newDigits);
              refs[i - 1]?.current?.focus();
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              refs[i - 1]?.current?.focus();
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              refs[i + 1]?.current?.focus();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              onChange((value + 1) % 1000);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              onChange((value + 999) % 1000);
            }
          }}
        />
      ))}
    </div>
  );
}
